/**
 * Community member detail — /community/members/:handleOrId
 *
 * Shows another member's profile + their 10 most-recent active threads.
 * Renders nothing posty for non-listed members other than profile basics.
 */
import * as React from "react";
import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";

import { API_BASE_URL, ApiError } from "@/lib/api";
import {
  fetchMember,
  type MemberDetailResponse,
} from "@/lib/community-api";
import { safeHref } from "@/lib/safeHref";
import { MarkdownBody } from "@/components/MarkdownBody";

type LoadState =
  | { kind: "loading" }
  | { kind: "ready"; data: MemberDetailResponse }
  | { kind: "not_member" }
  | { kind: "not_found" }
  | { kind: "error"; message: string };

function redirectToLogin(returnTo: string): void {
  const encoded = encodeURIComponent(returnTo);
  window.location.href = `${API_BASE_URL}/api/auth/login?return_to=${encoded}`;
}

export interface CommunityMemberPageProps {
  fetchFn?: typeof fetchMember;
  onUnauthenticated?: (returnTo: string) => void;
}

export function CommunityMemberPage({
  fetchFn = fetchMember,
  onUnauthenticated = redirectToLogin,
}: CommunityMemberPageProps = {}): React.JSX.Element {
  const params = useParams<{ handleOrId: string }>();
  const handleOrId = params.handleOrId ?? "";
  const [state, setState] = useState<LoadState>({ kind: "loading" });

  useEffect(() => {
    let cancelled = false;
    void (async () => {
      try {
        const data = await fetchFn(handleOrId);
        if (cancelled) return;
        setState({ kind: "ready", data });
      } catch (err) {
        if (cancelled) return;
        if (err instanceof ApiError && err.status === 401) {
          onUnauthenticated(`/community/members/${handleOrId}`);
          return;
        }
        if (err instanceof ApiError && err.status === 403) {
          setState({ kind: "not_member" });
          return;
        }
        if (err instanceof ApiError && err.status === 404) {
          setState({ kind: "not_found" });
          return;
        }
        const msg =
          err instanceof Error
            ? err.message
            : "Could not load this member's profile.";
        setState({ kind: "error", message: msg });
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [fetchFn, handleOrId, onUnauthenticated]);

  if (state.kind === "loading") {
    return (
      <section
        className="mx-auto max-w-3xl px-4 py-12 sm:px-6"
        aria-busy="true"
      >
        <p className="text-sm text-muted-foreground" role="status">
          Loading profile…
        </p>
      </section>
    );
  }

  if (state.kind === "not_member") {
    return (
      <SimpleErrorPage
        title="Community"
        message="The community is open to active learners and certified practitioners."
      />
    );
  }

  if (state.kind === "not_found") {
    return (
      <SimpleErrorPage
        title="Member not found"
        message="That handle or user ID doesn't match a community member."
      />
    );
  }

  if (state.kind === "error") {
    return (
      <section className="mx-auto max-w-3xl px-4 py-12 sm:px-6">
        <p className="text-sm text-red-700" role="alert">
          {state.message}
        </p>
      </section>
    );
  }

  const { profile, recent_threads } = state.data;

  return (
    <section className="mx-auto max-w-3xl px-4 py-12 sm:px-6">
      <nav className="text-xs text-muted-foreground">
        <Link to="/community/directory" className="hover:text-foreground">
          Directory
        </Link>{" "}
        <span aria-hidden="true">›</span> {profile.display_name}
      </nav>

      <header className="mt-3 border-b border-border pb-6">
        <div className="flex items-baseline gap-3">
          <h1 className="text-3xl font-bold text-foreground">
            {profile.display_name}
          </h1>
          {profile.credential_visible && profile.is_certified && (
            <span
              className="inline-flex items-center rounded-full bg-emerald-50 px-2 py-0.5 text-xs font-semibold text-emerald-700"
              title="Certified"
            >
              ✓ Certified
            </span>
          )}
        </div>
        {profile.handle && (
          <p className="mt-0.5 text-sm text-muted-foreground">
            @{profile.handle}
          </p>
        )}
        {profile.headline && (
          <p className="mt-3 text-base text-foreground">{profile.headline}</p>
        )}
        {profile.region && (
          <p className="mt-2 text-sm text-muted-foreground">
            {profile.region}
          </p>
        )}

        {profile.specialties.length > 0 && (
          <ul className="mt-4 flex flex-wrap gap-1">
            {profile.specialties.map((s) => (
              <li
                key={s}
                className="inline-flex items-center rounded-full bg-muted px-2 py-0.5 text-xs text-muted-foreground"
              >
                {s}
              </li>
            ))}
          </ul>
        )}

        {(() => {
          // Defense in depth: even though the backend rejects non-http(s)
          // schemes on save, scrub at render time too in case a stale row
          // exists or a future fetcher gets bypassed.
          const safeWebsite = safeHref(profile.website_url);
          const safeLinkedin = safeHref(profile.linkedin_url);
          if (!safeWebsite && !safeLinkedin) return null;
          return (
            <div className="mt-4 flex flex-wrap gap-3 text-xs">
              {safeWebsite && (
                <a
                  href={safeWebsite}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-primary underline"
                >
                  Website
                </a>
              )}
              {safeLinkedin && (
                <a
                  href={safeLinkedin}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-primary underline"
                >
                  LinkedIn
                </a>
              )}
            </div>
          );
        })()}

        {profile.bio && (
          <div className="mt-4">
            <MarkdownBody body={profile.bio} />
          </div>
        )}
      </header>

      <section className="mt-8">
        <h2 className="text-lg font-semibold text-foreground">
          Recent threads
        </h2>
        {recent_threads.length === 0 ? (
          <p className="mt-4 rounded-lg border border-dashed border-border p-4 text-sm text-muted-foreground">
            No active threads yet.
          </p>
        ) : (
          <ol className="mt-4 space-y-3">
            {recent_threads.map((t) => (
              <li key={t.id}>
                <Link
                  to={`/community/t/${t.id}`}
                  className="block rounded-lg border border-border bg-white p-4 hover:border-primary"
                >
                  <p className="text-xs uppercase tracking-wider text-muted-foreground">
                    {t.category_slug}
                  </p>
                  <h3 className="mt-1 text-base font-semibold text-foreground">
                    {t.title}
                  </h3>
                  <p className="mt-1 text-xs text-muted-foreground">
                    {t.reply_count}{" "}
                    {t.reply_count === 1 ? "reply" : "replies"} · last
                    activity{" "}
                    {new Date(t.last_activity_at).toLocaleDateString()}
                  </p>
                </Link>
              </li>
            ))}
          </ol>
        )}
      </section>
    </section>
  );
}

function SimpleErrorPage({
  title,
  message,
}: {
  title: string;
  message: string;
}): React.JSX.Element {
  return (
    <section className="mx-auto max-w-3xl px-4 py-16 sm:px-6 text-center">
      <h1 className="text-2xl font-bold text-foreground">{title}</h1>
      <p className="mt-4 text-muted-foreground">{message}</p>
      <Link
        to="/community/directory"
        className="mt-6 inline-flex items-center rounded-md border border-border px-4 py-2 text-sm font-medium hover:bg-muted"
      >
        Back to directory
      </Link>
    </section>
  );
}
